import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Badge,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  useToast,
  Icon,
} from '@chakra-ui/react';
import { usePagination, useSortBy, useTable } from 'react-table';
import { MdAdd, MdEdit, MdDelete, MdDownload } from 'react-icons/md';
import Card from 'components/card/Card';
import DateRangePicker from 'components/datePicker/DateRangePicker';
import { prettifyDate } from '../../../../functions/dateUtils';
import SalesAgreementsApi from '../../../../api/salesAgreements';
import AddSalesAgreementModal from './AddSalesAgreementModal';
import EditSalesAgreementModal from './EditSalesAgreementModal';

// Helper functions for parsing JSON fields
const parsePilotJson = (pilotJson) => {
  if (!pilotJson) return 'לא צוין';
  try {
    const pilot = JSON.parse(pilotJson);
    return pilot.enabled ? `פעיל - ${pilot.days} ימים` : 'לא פעיל';
  } catch {
    return 'לא זמין';
  }
};

const parseSetupFeeJson = (setupFeeJson) => {
  if (!setupFeeJson) return 'לא צוין';
  try {
    const setupFee = JSON.parse(setupFeeJson);
    const status = setupFee.isActive ? 'פעיל' : 'לא פעיל';
    return `₪${setupFee.amount.toLocaleString()} (${status})`;
  } catch {
    return 'לא זמין';
  }
};

const parseWithdrawalFeeJson = (withdrawalFeeJson) => {
  if (!withdrawalFeeJson) return 'לא צוין';
  try {
    const withdrawalFee = JSON.parse(withdrawalFeeJson);
    const paidByText = withdrawalFee.paidBy === 'employee' ? 'עובד' : 'מעסיק';
    return `₪${withdrawalFee.amount} - ${paidByText} - ${withdrawalFee.freeMonthlyCount || 0} חינם`;
  } catch {
    return 'לא זמין';
  }
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'לא צוין';
  return `₪${amount.toLocaleString()}`;
};

const SalesAgreementsModal = ({ isOpen, onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [agreementToDelete, setAgreementToDelete] = useState(null);
  const [filters, setFilters] = useState({
    companyName: '',
    planType: '',
    approveTerms: '',
    startDate: '',
    endDate: '',
    page: 1,
    resultsPerPage: 10,
  });

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const toast = useToast();
  const cancelRef = React.useRef();

  // Add Sales Agreement Modal
  const {
    isOpen: isAddModalOpen,
    onOpen: onAddModalOpen,
    onClose: onAddModalClose,
  } = useDisclosure();

  // Edit Sales Agreement Modal
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();

  // Delete Sales Agreement Modal
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  // Handle edit button click
  const handleEditClick = (agreement) => {
    setSelectedAgreement(agreement);
    onEditModalOpen();
  };

  // Handle delete button click
  const handleDeleteClick = (agreement) => {
    setAgreementToDelete(agreement);
    onDeleteModalOpen();
  };

  // Handle download button click - open PDF in new tab
  const handleDownloadClick = (agreement) => {
    if (agreement.agreementPdfUrl) {
      window.open(agreement.agreementPdfUrl, '_blank');
    } else {
      toast({
        title: 'PDF לא נמצא',
        description: 'לא קיים קובץ PDF להסכם זה',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      await SalesAgreementsApi.deleteSalesAgreement(agreementToDelete.id);

      toast({
        title: 'הסכם נמחק בהצלחה',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      // Refresh the table data
      fetchData();

      // Close modal and reset state
      onDeleteModalClose();
      setAgreementToDelete(null);
    } catch (error) {
      console.error('Error deleting sales agreement:', error);

      toast({
        title: 'שגיאה במחיקת הסכם',
        description: 'לא ניתן למחוק את ההסכם',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: 'מזהה חברה',
        accessor: 'companyId',
        width: '120px',
      },
      {
        Header: 'שם החברה',
        accessor: 'companyName',
        width: '200px',
      },
      {
        Header: 'איש קשר',
        accessor: 'contactName',
        width: '150px',
      },
      {
        Header: 'טלפון',
        accessor: 'contactPhone',
        width: '120px',
      },
      {
        Header: 'אימייל',
        accessor: 'contactEmail',
        width: '200px',
        Cell: ({ value }) => value || 'לא צוין',
      },
      {
        Header: 'אישור תנאים',
        accessor: 'approveTerms',
        width: '120px',
        Cell: ({ value }) => (
          <Badge colorScheme={value ? 'green' : 'red'}>
            {value ? 'כן' : 'לא'}
          </Badge>
        ),
      },
      {
        Header: 'סוג תוכנית',
        accessor: 'planType',
        width: '120px',
        Cell: ({ value }) => {
          if (!value) {
            return <Badge colorScheme="gray">לא צוין</Badge>;
          }
          const colorScheme = value === 'premium' ? 'purple' : value === 'advanced' ? 'blue' : 'gray';
          const displayText = value === 'premium' ? 'פרימיום' : value === 'advanced' ? 'מתקדם' : 'בסיסי';
          return <Badge colorScheme={colorScheme}>{displayText}</Badge>;
        },
      },
      {
        Header: 'עלות חודשית',
        accessor: 'monthlyFee',
        width: '120px',
        Cell: ({ value }) => formatCurrency(value),
      },
      {
        Header: 'תקופת פיילוט',
        accessor: 'pilotJson',
        width: '150px',
        Cell: ({ value }) => parsePilotJson(value),
      },
      {
        Header: 'עמלת הקמה',
        accessor: 'setupFeeJson',
        width: '150px',
        Cell: ({ value }) => parseSetupFeeJson(value),
      },
      {
        Header: 'עמלת משיכה',
        accessor: 'withdrawalFeeJson',
        width: '200px',
        Cell: ({ value }) => parseWithdrawalFeeJson(value),
      },
      {
        Header: 'תאריך יצירה',
        accessor: 'createdDate',
        width: '120px',
        Cell: ({ value }) => prettifyDate(value),
      },
      {
        Header: 'פעולות',
        accessor: 'actions',
        width: '200px',
        Cell: ({ row }) => (
          <Flex gap={1} wrap="nowrap" justify="flex-start">
            {row.original.agreementPdfUrl && (
              <Button
                size="sm"
                colorScheme="green"
                variant="ghost"
                onClick={() => handleDownloadClick(row.original)}
                title="הורדת PDF"
                minW="auto"
                px={2}
              >
                <Icon as={MdDownload} />
              </Button>
            )}
            <Button
              size="sm"
              colorScheme="blue"
              variant="ghost"
              onClick={() => handleEditClick(row.original)}
              title="עריכה"
              minW="auto"
              px={2}
            >
              <Icon as={MdEdit} />
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              variant="ghost"
              onClick={() => handleDeleteClick(row.original)}
              title="מחיקה"
              minW="auto"
              px={2}
            >
              <Icon as={MdDelete} />
            </Button>
          </Flex>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      pageCount,
      manualPagination: true,
      autoResetPage: false,
    },
    useSortBy,
    usePagination
  );

  const getSalesAgreementsReport = async (queryFilters) => {
    return await SalesAgreementsApi.getSalesAgreements(queryFilters);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const queryFilters = {
        ...filters,
        page: pageIndex + 1,
        resultsPerPage: pageSize,
      };

      // Remove empty filters
      Object.keys(queryFilters).forEach(key => {
        if (queryFilters[key] === '' || queryFilters[key] === null || queryFilters[key] === undefined) {
          delete queryFilters[key];
        }
      });

      const response = await getSalesAgreementsReport(queryFilters);
      if (response.data) {
        const agreements = response.data.agreements || [];
        setData(agreements);
        setPageCount(response.data.totalPages || 0);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching sales agreements:', error);
      setData([]);
      setPageCount(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, pageIndex, pageSize]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDateRangeChange = (dateRange) => {
    setFilters(prev => ({
      ...prev,
      startDate: dateRange?.from ? dateRange.from.toISOString().split('T')[0] : '',
      endDate: dateRange?.to ? dateRange.to.toISOString().split('T')[0] : '',
    }));
  };

  const applyFilters = () => {
    gotoPage(0);
    fetchData();
  };

  const resetFilters = () => {
    setFilters({
      companyName: '',
      planType: '',
      approveTerms: '',
      startDate: '',
      endDate: '',
      page: 1,
      resultsPerPage: 10,
    });
    gotoPage(0);
    setTimeout(fetchData, 100);
  };



  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="95vw" maxH="90vh">
          <ModalHeader>
            <Flex justify="space-between" align="center">
              <Heading size="lg" color={textColor}>
                הסכמי מכירות
              </Heading>
              <Button
                leftIcon={<Icon as={MdAdd} />}
                colorScheme="blue"
                onClick={onAddModalOpen}
              >
                הוספת הסכם חדש
              </Button>
            </Flex>
          </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <Card>
            <Flex direction="column" w="100%">
              {/* Filters */}
              <Flex gap={4} mb={6} wrap="wrap" align="end">
                <Box>
                  <Text mb={2} fontSize="sm" fontWeight="medium">
                    שם חברה
                  </Text>
                  <input
                    type="text"
                    placeholder="חפש לפי שם חברה"
                    value={filters.companyName}
                    onChange={(e) => handleFilterChange('companyName', e.target.value)}
                    style={{
                      width: '200px',
                      padding: '8px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px'
                    }}
                  />
                </Box>

                <Box>
                  <Text mb={2} fontSize="sm" fontWeight="medium">
                    סוג תוכנית
                  </Text>
                  <Select
                    placeholder="בחר סוג תוכנית"
                    value={filters.planType}
                    onChange={(e) => handleFilterChange('planType', e.target.value)}
                    width="200px"
                  >
                    <option value="basic">בסיסי</option>
                    <option value="advanced">מתקדם</option>
                    <option value="premium">פרימיום</option>
                  </Select>
                </Box>

                <Box>
                  <Text mb={2} fontSize="sm" fontWeight="medium">
                    אישור תנאים
                  </Text>
                  <Select
                    placeholder="בחר סטטוס אישור"
                    value={filters.approveTerms}
                    onChange={(e) => handleFilterChange('approveTerms', e.target.value)}
                    width="200px"
                  >
                    <option value="true">מאושר</option>
                    <option value="false">לא מאושר</option>
                  </Select>
                </Box>

                <Box>
                  <Text mb={2} fontSize="sm" fontWeight="medium">
                    טווח תאריכים
                  </Text>
                  <DateRangePicker
                    value={{
                      from: filters.startDate ? new Date(filters.startDate) : undefined,
                      to: filters.endDate ? new Date(filters.endDate) : undefined,
                    }}
                    onChange={handleDateRangeChange}
                    width="200px"
                  />
                </Box>

                <Button colorScheme="green" onClick={applyFilters} isLoading={loading}>
                  חפש
                </Button>
                <Button variant="outline" onClick={resetFilters}>
                  נקה
                </Button>
              </Flex>

              {/* Results count */}
              <Text mb={4} fontSize="sm" color="gray.600">
                נמצאו {totalCount} תוצאות
              </Text>

              {/* Table */}
              <Box overflowX="auto">
                <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
                  <Thead>
                    {headerGroups.map((headerGroup, index) => (
                      <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                        {headerGroup.headers.map((column, index) => (
                          <Th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            pe="10px"
                            key={index}
                            borderColor={borderColor}
                          >
                            <Flex
                              justify="space-between"
                              align="center"
                              fontSize={{ sm: '10px', lg: '12px' }}
                              color="gray.400"
                            >
                              {column.render('Header')}
                            </Flex>
                          </Th>
                        ))}
                      </Tr>
                    ))}
                  </Thead>
                  <Tbody {...getTableBodyProps()}>
                    {page.map((row, index) => {
                      prepareRow(row);
                      return (
                        <Tr {...row.getRowProps()} key={index}>
                          {row.cells.map((cell, index) => (
                            <Td
                              {...cell.getCellProps()}
                              key={index}
                              fontSize={{ sm: '14px' }}
                              minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                              borderColor="transparent"
                            >
                              <Text color={textColor} fontSize="sm" fontWeight="700">
                                {cell.render('Cell')}
                              </Text>
                            </Td>
                          ))}
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination */}
              <Flex justify="space-between" align="center" mt={4}>
                <Flex align="center" gap={2}>
                  <Text fontSize="sm">הצג</Text>
                  <Select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    width="80px"
                    size="sm"
                  >
                    {[10, 20, 30, 40, 50].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="sm">תוצאות</Text>
                </Flex>

                <Flex align="center" gap={2}>
                  <Button
                    size="sm"
                    onClick={() => gotoPage(0)}
                    isDisabled={!canPreviousPage}
                  >
                    {'<<'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => previousPage()}
                    isDisabled={!canPreviousPage}
                  >
                    {'<'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => nextPage()}
                    isDisabled={!canNextPage}
                  >
                    {'>'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => gotoPage(pageCount - 1)}
                    isDisabled={!canNextPage}
                  >
                    {'>>'}
                  </Button>
                </Flex>

                <Flex align="center" gap={2}>
                  <Text fontSize="sm">עמוד</Text>
                  <NumberInput
                    value={pageIndex + 1}
                    onChange={(_, value) => {
                      const page = value ? value - 1 : 0;
                      gotoPage(page);
                    }}
                    size="sm"
                    width="80px"
                    min={1}
                    max={pageOptions.length}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm">מתוך {pageOptions.length}</Text>
                </Flex>
              </Flex>
            </Flex>
          </Card>
        </ModalBody>
      </ModalContent>
    </Modal>

    {/* Add Sales Agreement Modal */}
    <AddSalesAgreementModal
      isOpen={isAddModalOpen}
      onClose={onAddModalClose}
      onSuccess={() => {
        onAddModalClose();
        fetchData(); // Refresh the table data
      }}
    />

    {/* Edit Sales Agreement Modal */}
    <EditSalesAgreementModal
      isOpen={isEditModalOpen}
      onClose={onEditModalClose}
      agreement={selectedAgreement}
      onSuccess={() => {
        onEditModalClose();
        fetchData(); // Refresh the table data
      }}
    />

    {/* Delete Confirmation Dialog */}
    <AlertDialog
      isOpen={isDeleteModalOpen}
      onClose={onDeleteModalClose}
      leastDestructiveRef={cancelRef}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            מחיקת הסכם מכירות
          </AlertDialogHeader>

          <AlertDialogBody>
            האם ברצונך למחוק את הסכם המכירות של חברה{' '}
            <strong>{agreementToDelete?.companyName}</strong>?
            <br />
            פעולה זו אינה ניתנת לביטול.
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onDeleteModalClose}>
              ביטול
            </Button>
            <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
              מחיקה
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    </>
  );
};

export default SalesAgreementsModal;
